/**
 * FlagActions — the non-notice actions on a Wisconsin contingency (copy §3;
 * legal review Q-L2, Q-L3; design T12). Each one flags the row for both
 * parties and stops automatic updates; none of them decides anything.
 *
 *   offplatform_notice     "I delivered a notice another way" (upload a copy, date, method)
 *   cure_dispute           "I disagree that this condition can be cured" (buyer, on a cured row)
 *   land_division_dispute  "I disagree with this record" (the other party, after an approval record)
 *   not_applicable_dispute "I disagree with this record" (buyer, on a lead-paint row recorded as
 *                          not applying; copy §15.1)
 *
 * The buttons shown are exactly the server's `flag_actions`.
 */

import React, { useState } from 'react';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_PURPOSES,
  FLAG_ACTIONS,
  MAX_UPLOAD_BYTES,
  OFFPLATFORM_METHODS,
  disputeCure,
  disputeLandDivision,
  disputeNotApplicable,
  noticeErrorInfo,
  reportOffplatformNotice,
  uploadContingencyAttachment,
} from '../../../agents/contingencyNoticesAgent';
import ContractDialog from './ContractDialog';
import { contractCopy } from './contractCopy';
import { centralToday } from './contractFormat';
import styles from './FlagActions.module.css';

/** Copy §4 `WI.offplatform.method.*`, one per OFFPLATFORM_METHODS value. */
const methodLabel = (m) => contractCopy(`WI.offplatform.method.${m}`) || m;

const ACTION_LABEL_KEY = {
  [FLAG_ACTIONS.OFFPLATFORM_NOTICE]: 'WI.offplatform.action',
  [FLAG_ACTIONS.CURE_DISPUTE]: 'WI.cure.dispute.action',
  [FLAG_ACTIONS.LAND_DIVISION_DISPUTE]: 'WI.land_division.dispute.action',
  [FLAG_ACTIONS.NOT_APPLICABLE_DISPUTE]: 'WI.lead_paint.not_applicable_dispute.action',
};

/** The dispute flags: what the confirm says, and the call that records it. */
const DISPUTES = {
  [FLAG_ACTIONS.CURE_DISPUTE]: { confirmKey: 'WI.cure.dispute.confirm', call: disputeCure },
  [FLAG_ACTIONS.LAND_DIVISION_DISPUTE]: { confirmKey: 'WI.land_division.dispute.confirm', call: disputeLandDivision },
  [FLAG_ACTIONS.NOT_APPLICABLE_DISPUTE]: {
    confirmKey: 'WI.lead_paint.not_applicable_dispute.confirm', call: disputeNotApplicable,
  },
};

function OffPlatformDialog({ contingency, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [deliveredOn, setDeliveredOn] = useState('');
  const [method, setMethod] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A retry after a failed report reuses the upload instead of storing the file twice.
  const [uploaded, setUploaded] = useState(null);
  const today = centralToday();
  const id = contingency.id;

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    if (!file || !deliveredOn || !method) {
      setError('Add the copy, the date and how you delivered it.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`${file.name} is larger than 25 MB.`);
      return;
    }
    setBusy(true);
    try {
      let copy = uploaded && uploaded.file === file ? uploaded.data : null;
      if (!copy) {
        copy = await uploadContingencyAttachment(id, file, ATTACHMENT_PURPOSES.OFFPLATFORM);
        setUploaded({ file, data: copy });
      }
      const result = await reportOffplatformNotice(id, {
        attachmentId: copy.file_id, deliveredOn, method, description,
      });
      onDone(result);
    } catch (err) {
      setError(noticeErrorInfo(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ContractDialog
      title={contractCopy('WI.offplatform.action')}
      eyebrow={contingency.contract?.heading}
      onClose={onClose}
      busy={busy}
      testId="offplatform-dialog"
    >
      <form onSubmit={submit} className={styles.form} noValidate>
        <p className={styles.help}>{contractCopy('WI.offplatform.help')}</p>
        <div className={styles.field}>
          <label htmlFor={`op-file-${id}`} className={styles.label}>Copy of the notice you delivered</label>
          <input
            id={`op-file-${id}`}
            type="file"
            accept={ATTACHMENT_ACCEPT}
            required
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="offplatform-file"
          />
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor={`op-date-${id}`} className={styles.label}>Date you delivered it</label>
            <input
              id={`op-date-${id}`}
              type="date"
              className={styles.input}
              max={today}
              required
              value={deliveredOn}
              onChange={(e) => setDeliveredOn(e.target.value)}
              data-testid="offplatform-date"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor={`op-method-${id}`} className={styles.label}>{contractCopy('WI.offplatform.method.label')}</label>
            <select
              id={`op-method-${id}`}
              className={styles.input}
              required
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              data-testid="offplatform-method"
            >
              <option value="">Choose…</option>
              {OFFPLATFORM_METHODS.map((m) => <option key={m} value={m}>{methodLabel(m)}</option>)}
            </select>
          </div>
        </div>
        <div className={styles.field}>
          <label htmlFor={`op-desc-${id}`} className={styles.label}>
            Anything else to record <span className={styles.optional}>(optional)</span>
          </label>
          <textarea
            id={`op-desc-${id}`}
            className={styles.textarea}
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className={styles.primary} disabled={busy} data-testid="offplatform-submit">
            {busy ? 'Recording…' : 'Record it'}
          </button>
        </div>
      </form>
    </ContractDialog>
  );
}

function DisputeDialog({ contingency, action, onClose, onDone }) {
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const dispute = DISPUTES[action];
  const id = contingency.id;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await dispute.call(id, statement);
      onDone(result);
    } catch (err) {
      setError(noticeErrorInfo(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ContractDialog
      title={contractCopy(ACTION_LABEL_KEY[action])}
      eyebrow={contingency.contract?.heading}
      onClose={onClose}
      busy={busy}
      testId="dispute-dialog"
    >
      <p className={styles.help} data-testid="dispute-confirm-text">{contractCopy(dispute.confirmKey)}</p>
      <div className={styles.field}>
        <label htmlFor={`dispute-${id}`} className={styles.label}>
          Your reason <span className={styles.optional}>(optional)</span>
        </label>
        <textarea
          id={`dispute-${id}`}
          className={styles.textarea}
          rows={3}
          maxLength={2000}
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
        />
      </div>
      {error && <p className={styles.error} role="alert">{error}</p>}
      <div className={styles.actions}>
        <button type="button" className={styles.secondary} onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className={styles.primary} onClick={confirm} disabled={busy} data-testid="dispute-confirm">
          {busy ? 'Flagging…' : 'Flag it'}
        </button>
      </div>
    </ContractDialog>
  );
}

export default function FlagActions({ contingency, onChanged }) {
  const [open, setOpen] = useState(null);
  const actions = (contingency.contract?.flag_actions || []).filter((a) => ACTION_LABEL_KEY[a]);
  if (actions.length === 0) return null;

  const done = (result) => {
    setOpen(null);
    onChanged?.(result);
  };

  return (
    <div className={styles.flags} data-testid={`flag-actions-${contingency.id}`}>
      {actions.map((action) => (
        <button
          key={action}
          type="button"
          className={styles.flagBtn}
          onClick={() => setOpen(action)}
          data-testid={`flag-${action}-${contingency.id}`}
        >
          {contractCopy(ACTION_LABEL_KEY[action])}
        </button>
      ))}
      {open === FLAG_ACTIONS.OFFPLATFORM_NOTICE && (
        <OffPlatformDialog contingency={contingency} onClose={() => setOpen(null)} onDone={done} />
      )}
      {DISPUTES[open] && (
        <DisputeDialog contingency={contingency} action={open} onClose={() => setOpen(null)} onDone={done} />
      )}
    </div>
  );
}
